<template>
  <!-- The 10 Year Plan Section -->
  <section class="content-section animate fadeInUp">
    <h2 class="text-center">The 10 Year Plan</h2>
    <p>
      The33Path's first decade is structured as a long-form narrative arc in
      which micro-stories give way to a macro-tale, evolving in real time as the
      consciousness behind the project evolves. Over this ten-year span, twelve
      cinematic works unfold as one unified transmission, guiding the audience
      through a journey of human evolution, identity, and mythic storytelling.
    </p>
    <blockquote class="serif quote animate fadeInUp text-right">
      During this timeframe, Patricia becomes the physical vessel for her three
      characters, allowing the pulse of her consciousness - and the stories of
      these identities - to take the public through a journey of expansion and
      awakening. Other actors will also embody recurring presences within this
      universe, appearing according to their resonance with each character and
      story.
    </blockquote>
    <p>
      The macro-structure of this decade-long content cycle is expressed through
      three evolving characters who shapeshift across twelve cinematic projects.
      The first,
      <span class="serif textColour animate fadeInDown inlineBlock uppercase"
        >CECILIA</span
      >, is a work of art that becomes human. The second,
      <span class="serif textColour animate fadeInDown inlineBlock uppercase"
        >PATRICIA RIBEIRO</span
      >, is the human who creates works of art. The third,
      <span class="serif textColour animate fadeInDown inlineBlock uppercase"
        >THE PATH</span
      >, is the brand's eponymous consciousness - spirit awakening fully within
      matter to reveal the mysteries encoded in her holographic reality.
    </p>
    <p class="text-center smallFontSize bigMarginTop">
      Click on each character's poster to explore its narrative function and its
      four films. This section is restricted to confirmed investors and
      collaborators under NDA.
    </p>
    <!-- CSS Posters with captions -->
    <div class="image-containerRow">
      <!-- Character 1 - Cecilia -->
      <div class="image-wrapper column" @click="openDrawer('cecilia')">
        <div class="poster-container cecilia-poster animate zoomIn">
          <div class="poster-text">CECILIA</div>
        </div>
      </div>

      <!-- Character 2 - Patricia -->
      <div class="image-wrapper column" @click="openDrawer('patricia')">
        <div class="poster-container patricia-poster animate zoomIn">
          <div class="poster-text">PATRICIA</div>
          <div class="poster-subtext">&<br />HER ALTER EGOS</div>
        </div>
      </div>

      <!-- Character 3 - The Path -->
      <div class="image-wrapper column" @click="openDrawer('thePath')">
        <div class="poster-container thepath-poster animate zoomIn">
          <div class="poster-text">THE PATH</div>
        </div>
      </div>
    </div>

    <small class="grey-dark initialLineHeight">
      The full progression of the three characters is already mapped, allowing
      precise long-term planning for development, production, and release
      rhythm. This structure respects the complexity of each project and ensures
      continuous audience engagement across the macro-narrative and all layers
      of the brand's ecosystem.</small
    >
    <small class="grey-dark initialLineHeight bigMarginTop">
      Pre-treatments, treatments, and detailed creative blueprints for all
      twelve stories are contained within this section and are accessible only
      to confirmed investors and collaborators under NDA.</small
    >
  </section>

  <!-- BottomDrawer Component Usage -->
  <BottomDrawer :isOpen="activeDrawer !== null" @closeDrawer="closeDrawer">
    <!-- For each poster, we conditionally display details based on user access -->
    <template v-if="activeDrawer === 'cecilia'">
      <div class="largestMarginTop" v-if="canAccessCharacter1">
        <CeciliaCharacter />
      </div>
      <div class="gated-drawer-wrapper" v-else>
        <h3 class="serif bigText text-center">Cecilia</h3>
        <div class="restricted-drawer-content-simple">
          <p v-if="!isCeciliaEnabled" class="restricted-message">
            Coming soon.
          </p>

          <!-- For Visitors: always show Request Access -->
          <button
            v-if="userRole === 'visitor'"
            class="request-access-button"
            @click="requestContentAccess('10YearPlan-Cecilia', 'Cecilia')"
            :disabled="isProcessing"
          >
            <span v-if="isProcessing">Processing...</span>
            <span v-else>Request Access</span>
          </button>

          <!-- For VIPs: only show "Notify me" if content is disabled -->
          <button
            v-if="userRole === 'vip' && !isCeciliaEnabled"
            class="notify-button"
            @click="requestContentAccess('10YearPlan-Cecilia', 'Cecilia')"
            :disabled="isProcessing"
          >
            <span v-if="isProcessing">Processing...</span>
            <span v-else>Notify me when available</span>
          </button>
        </div>
      </div>
    </template>

    <template v-else-if="activeDrawer === 'patricia'">
      <div class="largestMarginTop" v-if="canAccessCharacter2">
        <PatriciaCharacter />
      </div>
      <div class="gated-drawer-wrapper" v-else>
        <h3 class="serif bigText text-center">Patricia & Her Alter Egos</h3>
        <div class="restricted-drawer-content-simple">
          <p v-if="!isPatriciaEnabled" class="restricted-message">
            Coming soon.
          </p>

          <!-- For Visitors: always show Request Access -->
          <button
            v-if="userRole === 'visitor'"
            class="request-access-button"
            @click="requestContentAccess('10YearPlan-Patricia', 'Patricia')"
            :disabled="isProcessing"
          >
            <span v-if="isProcessing">Processing...</span>
            <span v-else>Request Access</span>
          </button>

          <!-- For VIPs: only show "Notify me" if content is disabled -->
          <button
            v-if="userRole === 'vip' && !isPatriciaEnabled"
            class="notify-button"
            @click="requestContentAccess('10YearPlan-Patricia', 'Patricia')"
            :disabled="isProcessing"
          >
            <span v-if="isProcessing">Processing...</span>
            <span v-else>Notify me when available</span>
          </button>
        </div>
      </div>
    </template>

    <template v-else-if="activeDrawer === 'thePath'">
      <div class="largestMarginTop" v-if="canAccessCharacter3">
        <ThePathCharacter />
      </div>
      <div class="gated-drawer-wrapper" v-else>
        <h3 class="serif bigText text-center">The Path</h3>
        <div class="restricted-drawer-content-simple">
          <p v-if="!isThePathEnabled" class="restricted-message">
            Coming soon.
          </p>

          <!-- For Visitors: always show Request Access -->
          <button
            v-if="userRole === 'visitor'"
            class="request-access-button"
            @click="requestContentAccess('10YearPlan-thePath', 'The Path')"
            :disabled="isProcessing"
          >
            <span v-if="isProcessing">Processing...</span>
            <span v-else>Request Access</span>
          </button>

          <!-- For VIPs: only show "Notify me" if content is disabled -->
          <button
            v-if="userRole === 'vip' && !isThePathEnabled"
            class="notify-button"
            @click="requestContentAccess('10YearPlan-thePath', 'The Path')"
            :disabled="isProcessing"
          >
            <span v-if="isProcessing">Processing...</span>
            <span v-else>Notify me when available</span>
          </button>
        </div>
      </div>
    </template>
  </BottomDrawer>

  <!-- Feedback Toast for all request actions -->
  <FeedbackToast
    :message="accessRequestedMessage"
    :variant="feedbackVariant"
    :show="showFeedbackToast"
    @dismissed="dismissFeedback"
  />
</template>

<script>
import { onMounted, ref } from "vue";
import BottomDrawer from "@/components/GenericComponents/BottomDrawer.vue";
import FeedbackToast from "@/components/GenericComponents/FeedbackToast.vue";
import { doc, getDoc } from "firebase/firestore";
import { db, auth } from "@/firebase";
import { canAccessContent, getUserRole } from "@/firebase/useContent";
import { requestContentAccess } from "@/firebase/useContentRequests";
import CeciliaCharacter from "@/components/ThePathUniverse/CeciliaCharacter.vue";
import PatriciaCharacter from "@/components/ThePathUniverse/PatriciaCharacter.vue";
import ThePathCharacter from "@/components/ThePathUniverse/ThePathCharacter.vue";
import SwissStyleTable from "@/components/GenericComponents/SwissStyleTable.vue";

export default {
  name: "The10YearPlan",
  components: {
    BottomDrawer,
    FeedbackToast,
    CeciliaCharacter,
    PatriciaCharacter,
    ThePathCharacter,
    SwissStyleTable,
  },
  setup() {
    // Image sources (kept for backup)
    const cecilia = ref(require("@/assets/thePath/Posters/CECILIA.jpg"));
    const patricia = ref(require("@/assets/thePath/Posters/PATRICIA.jpg"));
    const thePath = ref(require("@/assets/thePath/Posters/THE_PATH.jpg"));

    // Bottom drawer state
    const activeDrawer = ref(null);

    // State for content access
    const canAccessCharacter1 = ref(false);
    const canAccessCharacter2 = ref(false);
    const canAccessCharacter3 = ref(false);
    const userRole = ref("visitor");
    const isLoading = ref(true);

    // Content enabled status
    const isCeciliaEnabled = ref(true);
    const isPatriciaEnabled = ref(true);
    const isThePathEnabled = ref(true);

    // Access control: determine if the user has VIP or admin privileges
    const hasAccess = ref(false);
    const accessRequestedMessage = ref("");
    const showFeedbackToast = ref(false);
    const feedbackVariant = ref("success");
    const isProcessing = ref(false);

    // Dismiss feedback toast
    function dismissFeedback() {
      showFeedbackToast.value = false;
      accessRequestedMessage.value = "";
    }

    // Message to display when content is restricted
    const restrictedMessage = ref(
      "This content is currently in limited release. Contact our distribution team for screening access."
    );

    function openDrawer(character) {
      activeDrawer.value = character;
      document.body.style.overflow = "hidden";

      // Log access conditions for the character
      if (character === "cecilia") {
      } else if (character === "patricia") {
      } else if (character === "thePath") {
      }
    }

    function closeDrawer() {
      activeDrawer.value = null;
      document.body.style.overflow = "auto";
    }

    // Check access to all characters
    async function checkAccess() {
      isLoading.value = true;
      try {
        // Get user role if logged in
        if (auth.currentUser) {
          userRole.value = await getUserRole(auth.currentUser.uid);

          // Check if user has VIP or admin access for drawer content
          hasAccess.value =
            userRole.value === "vip" || userRole.value === "admin";
        } else {
          userRole.value = "visitor";
          hasAccess.value = false;
        }

        // Get enabled status for each content section
        const ceciliaDoc = await getDoc(
          doc(db, "content_settings", "10YearPlan-Cecilia")
        );
        if (ceciliaDoc.exists()) {
          isCeciliaEnabled.value = ceciliaDoc.data().enabled;
        }

        const patriciaDoc = await getDoc(
          doc(db, "content_settings", "10YearPlan-Patricia")
        );
        if (patriciaDoc.exists()) {
          isPatriciaEnabled.value = patriciaDoc.data().enabled;
        }

        const thePathDoc = await getDoc(
          doc(db, "content_settings", "10YearPlan-thePath")
        );
        if (thePathDoc.exists()) {
          isThePathEnabled.value = thePathDoc.data().enabled;
        }

        // Check access for each character
        canAccessCharacter1.value = await canAccessContent(
          "10YearPlan-Cecilia",
          userRole.value
        );

        canAccessCharacter2.value = await canAccessContent(
          "10YearPlan-Patricia",
          userRole.value
        );

        canAccessCharacter3.value = await canAccessContent(
          "10YearPlan-thePath",
          userRole.value
        );

      } catch (error) {
        // Default to no access on error
        canAccessCharacter1.value = false;
        canAccessCharacter2.value = false;
        canAccessCharacter3.value = false;
        hasAccess.value = false;
      } finally {
        isLoading.value = false;
      }
    }

    // Request Access using our new service
    async function handleContentRequest(contentId, contentTitle) {
      // Prevent double-clicks
      if (isProcessing.value) return;

      isProcessing.value = true;

      if (auth.currentUser) {
        try {
          const success = await requestContentAccess(
            contentId,
            contentTitle,
            userRole.value
          );

          if (success) {
            accessRequestedMessage.value = "Request sent successfully.";
            feedbackVariant.value = "success";
            showFeedbackToast.value = true;
          } else {
            accessRequestedMessage.value =
              "Failed to send request. Please try again.";
            feedbackVariant.value = "error";
            showFeedbackToast.value = true;
          }
        } catch (error) {
          accessRequestedMessage.value = "An error occurred. Please try again.";
          feedbackVariant.value = "error";
          showFeedbackToast.value = true;
        } finally {
          isProcessing.value = false;
        }
      } else {
        isProcessing.value = false;
      }
    }

    onMounted(() => {
      checkAccess();

      // Setup intersection observer for animations
      const elements = document.querySelectorAll(".animate");
      const observerOptions = {
        root: null,
        rootMargin: "-10% 0px",
        threshold: 0,
      };

      const handleIntersection = (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          } else {
            entry.target.classList.remove("active");
          }
        });
      };

      const observer = new IntersectionObserver(
        handleIntersection,
        observerOptions
      );
      elements.forEach((el) => {
        if (el) observer.observe(el);
      });
    });

    return {
      cecilia,
      patricia,
      thePath,
      activeDrawer,
      openDrawer,
      closeDrawer,
      hasAccess,
      requestContentAccess: handleContentRequest,
      accessRequestedMessage,
      showFeedbackToast,
      feedbackVariant,
      dismissFeedback,
      canAccessCharacter1,
      canAccessCharacter2,
      canAccessCharacter3,
      isCeciliaEnabled,
      isPatriciaEnabled,
      isThePathEnabled,
      restrictedMessage,
      isLoading,
      userRole,
      isProcessing,
    };
  },
};
</script>

<style scoped>
.content-section {
  margin-bottom: 3rem;
}

/* Restricted Access Styles for BottomDrawer */
.no-access-message {
  font-size: var(--smallFontSize);
  text-align: center;
  margin-bottom: 1rem;
}

.access-request-message {
  margin-top: 0.5rem;
  font-size: var(--smallFontSize);
}

.access-request-message.success {
  color: var(--green);
}

.access-request-message.error {
  color: var(--red);
}

/* Content width */
.largestMarginTop,
.column,
.restricted-drawer-content-simple {
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
}

/* Image Posters */
.image-containerRow {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  margin: var(--doubleSpace) 0;
}
.image-wrapper {
  width: 30%;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
}

.image-wrapper:hover .characterImg {
  transform: scale(1.01);
}

.characterImg {
  width: 100%;
  height: auto;
  object-fit: cover;
  transition: transform 0.3s ease-in-out;
  border-radius: var(--border-radius-medium);
}

/* Restricted content styling */
.restricted {
  cursor: default;
}

.restricted-content {
  width: 100%;
  aspect-ratio: 3/4;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: var(--border-radius-medium);
  padding: var(--largeSpace);
  margin-bottom: var(--space);
}

/* Gated drawer wrapper - vertically centers content */
.gated-drawer-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 120px); /* Account for footer */
  padding: var(--largeSpace) 0;
}

/* Simplified restricted drawer content */
.restricted-drawer-content-simple {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: var(--space);
  padding: var(--largeSpace);
}

.restricted-message {
  color: var(--text-white);
  text-align: center;
  font-size: var(--smallFontSize);
  font-style: italic;
}

/* CSS Poster Styles */
.poster-container {
  width: 100%;
  aspect-ratio: 1/1.414;
  display: flex;
  flex-direction: column;
  padding-top: 55%;
  align-items: center;
  transition: transform var(--fastSpeed) ease-in-out;
  border-radius: var(--border-radius-medium);
  border: 1px solid var(--grey-lightest);
  /* Light gray background matching the JPGs */
}

.image-wrapper:hover .poster-container {
  transform: scale(1.01);
}

.poster-text,
.poster-subtext {
  font-family: "Inter", sans-serif;
  letter-spacing: 0.1em;
  text-align: center;
}

.poster-text {
  font-size: calc(var(--biggerFontSize) * 0.9);
  font-weight: 630;
}

.poster-subtext {
  font-size: calc(var(--regularFontSize) * 0.85);
  font-weight: 260;
  line-height: 1.7rem;
}

/* Responsive font sizes already handled by root variables */

@media screen and (max-width: 790px) {
  .poster-text {
    font-size: var(--bigFontSize);
  }
  .poster-subtext {
    font-weight: 200;
    line-height: 1.4rem;
  }
}

@media screen and (max-width: 610px) {
  .poster-text {
    font-size: calc(var(--largeFontSize) * 0.9);
    font-weight: 630;
  }

  .poster-subtext {
    font-size: calc(var(--regularFontSize) * 0.65);

    font-weight: 230;
    line-height: 1.2rem;
  }
}
</style>
